'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type {
  CurrentUserHrContext,
  Employee,
  EmployeeFullReview,
  EmployeeFullCertification,
  EmployeeEmergencyContact,
  EmployeeAddress,
  EmployeeTimeOffBalance,
  EmployeeCompensationEntry,
  EmployeeDocument,
  EmployeeTimeOffRequest,
} from '@/app/hr/components/types';

interface UseMyHrDataResult {
  loading: boolean;
  error: string | null;
  notLinked: boolean;
  data: CurrentUserHrContext | null;
  reload: () => void;
}

export function useMyHrData(): UseMyHrDataResult {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notLinked, setNotLinked] = useState(false);
  const [data, setData] = useState<CurrentUserHrContext | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const supabase = createClient();

  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    if (!currentUser?.id) return;

    let cancelled = false;

    const safeQuery = async <T,>(fn: () => Promise<{ data: T | null; error: any }>, label: string): Promise<T[]> => {
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data, error } = await fn();
        if (!error) return (data ?? []) as T[];
        const status = String((error as any)?.status ?? '');
        const isRateLimit = status.includes('429') || (error as any)?.message?.toLowerCase()?.includes('rate');
        if (!isRateLimit) {
          console.warn(`[MyHrData] ${label} failed:`, error);
          return [];
        }
        await new Promise(r => setTimeout(r, 200 * Math.pow(2, attempt)));
      }
      return [];
    };

    (async () => {
      setLoading(true);
      setError(null);
      setNotLinked(false);

      try {
        // Step 1 — Look up the user_profile for the logged-in user
        const { data: profile, error: profErr } = await supabase
          .from('user_profiles')
          .select('id, employee_id, first_name, last_name')
          .eq('id', currentUser.id)
          .maybeSingle();

        if (profErr) throw profErr;
        if (!profile) {
          if (!cancelled) { setNotLinked(true); setLoading(false); }
          return;
        }
        if (!profile.employee_id) {
          if (!cancelled) { setNotLinked(true); setLoading(false); }
          return;
        }

        const eid = profile.employee_id as string;

        // Step 2 — Load employee record
        const { data: empData, error: empErr } = await supabase
          .from('employees')
          .select('*')
          .eq('id', eid)
          .maybeSingle();
        if (empErr) throw empErr;
        if (!empData) {
          if (!cancelled) { setNotLinked(true); setLoading(false); }
          return;
        }
        const employee = empData as Employee;

        // Step 3 — Sequentially load related data with 80ms gaps
        await new Promise(r => setTimeout(r, 80));
        const reviews = await safeQuery<EmployeeFullReview>(
          () => supabase.from('employee_reviews').select('*').eq('employee_id', eid).neq('status', 'draft'),
          'reviews'
        );

        await new Promise(r => setTimeout(r, 80));
        const certifications = await safeQuery<EmployeeFullCertification>(
          () => supabase.from('employee_certifications').select('*').eq('employee_id', eid),
          'certifications'
        );

        await new Promise(r => setTimeout(r, 80));
        const emergencyContacts = await safeQuery<EmployeeEmergencyContact>(
          () => supabase.from('employee_emergency_contacts').select('*').eq('employee_id', eid),
          'emergency_contacts'
        );

        await new Promise(r => setTimeout(r, 80));
        const addresses = await safeQuery<EmployeeAddress>(
          () => supabase.from('employee_addresses').select('*').eq('employee_id', eid),
          'addresses'
        );

        await new Promise(r => setTimeout(r, 80));
        const timeOffBalances = await safeQuery<EmployeeTimeOffBalance>(
          () => supabase.from('employee_time_off_balances').select('*').eq('employee_id', eid),
          'time_off_balances'
        );

        await new Promise(r => setTimeout(r, 80));
        const compensationHistory = await safeQuery<EmployeeCompensationEntry>(
          () => supabase.from('employee_compensation_history').select('*').eq('employee_id', eid),
          'compensation_history'
        );

        await new Promise(r => setTimeout(r, 80));
        const documents = await safeQuery<EmployeeDocument>(
          () => supabase.from('employee_documents').select('*').eq('employee_id', eid).eq('is_archived', false),
          'documents'
        );

        await new Promise(r => setTimeout(r, 80));
        const ptoRequests = await safeQuery<EmployeeTimeOffRequest>(
          () => supabase.from('employee_time_off_requests').select('*').eq('employee_id', eid).order('created_at', { ascending: false }),
          'pto_requests'
        );

        // Step 4 — Load supervisor record if exists
        let supervisor: Employee | null = null;
        if (employee.supervisor_id) {
          await new Promise(r => setTimeout(r, 80));
          const { data: supData } = await supabase
            .from('employees')
            .select('*')
            .eq('id', employee.supervisor_id)
            .maybeSingle();
          if (supData) supervisor = supData as Employee;
        }

        if (cancelled) return;

        setData({
          user_profile_id: profile.id,
          employee_id: eid,
          employee,
          supervisor,
          reviews,
          certifications,
          emergencyContacts,
          addresses,
          timeOffBalances,
          compensationHistory,
          documents,
          ptoRequests,
        });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('[MyHrData] Top-level load error:', err);
          setError(err instanceof Error ? err.message : 'Failed to load HR data');
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser?.id, reloadKey]);

  return { loading, error, notLinked, data, reload };
}
